<script setup>
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import StatBarChart from "@/components/StatBarChart.vue";
import {
  getStatDetailData,
  STAT_DETAIL_META,
} from "@/Scripts/statDetailFunctions";

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const loading = ref(true);
const error = ref("");
const detail = ref(null);
const rangeOptions = Object.freeze([
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 180, label: "6 months" },
]);
const allowedRanges = new Set(rangeOptions.map((option) => option.days));

function parseRange(value) {
  const days = Number(value);
  return allowedRanges.has(days) ? days : 30;
}

const selectedDays = ref(parseRange(route.query.range));
const rangeLabel = computed(
  () =>
    rangeOptions.find((option) => option.days === selectedDays.value)?.label ||
    "30 days"
);

const statKey = computed(() => String(route.params.stat || ""));
const meta = computed(() => STAT_DETAIL_META[statKey.value] || null);
let requestSequence = 0;

function selectRange(days) {
  if (!allowedRanges.has(days) || selectedDays.value === days) return;
  selectedDays.value = days;
  router.replace({
    query: { ...route.query, range: String(days) },
  });
}

async function loadDetail() {
  const requestId = ++requestSequence;
  if (!meta.value) {
    router.replace("/home");
    return;
  }

  loading.value = true;
  error.value = "";
  detail.value = null;
  try {
    let userId = userStore?.user?.id || null;
    if (!userId) {
      const result = await userStore.refreshFromSession();
      userId = result?.user?.id || null;
    }
    if (!userId) throw new Error("Unable to load your account.");
    const nextDetail = await getStatDetailData(
      userId,
      statKey.value,
      selectedDays.value
    );
    if (requestId !== requestSequence) return;
    detail.value = nextDetail;
  } catch (err) {
    if (requestId !== requestSequence) return;
    console.error("Failed to load stat details:", err);
    error.value = "This statistic could not be loaded. Please try again.";
  } finally {
    if (requestId === requestSequence) loading.value = false;
  }
}

watch(
  () => route.query.range,
  (value) => {
    const nextRange = parseRange(value);
    if (nextRange !== selectedDays.value) selectedDays.value = nextRange;
  }
);
watch([statKey, selectedDays], loadDetail, { immediate: true });
</script>

<template>
  <main class="stat-detail-page">
    <div class="stat-detail-shell">
      <button class="back-button" type="button" @click="router.push('/home')">
        <span aria-hidden="true">←</span> Back to progress
      </button>

      <div v-if="loading" class="state-card" role="status">
        <div class="loading-line loading-title"></div>
        <div class="loading-line loading-value"></div>
        <div class="loading-line"></div>
      </div>

      <div v-else-if="error" class="state-card error-card">
        <h1>Something went wrong</h1>
        <p>{{ error }}</p>
        <button type="button" class="retry-button" @click="loadDetail">
          Try again
        </button>
      </div>

      <template v-else-if="detail && meta">
        <header class="detail-header">
          <div>
            <p class="eyebrow">Progress detail</p>
            <h1>{{ meta.title }}</h1>
          </div>
          <div
            v-if="meta.hasRange"
            class="range-selector"
            role="group"
            aria-label="Chart date range"
          >
            <button
              v-for="option in rangeOptions"
              :key="option.days"
              type="button"
              class="range-button"
              :class="{ active: selectedDays === option.days }"
              :aria-pressed="selectedDays === option.days"
              @click="selectRange(option.days)"
            >
              {{ option.label }}
            </button>
          </div>
          <span v-else class="range-chip">Current</span>
        </header>

        <section class="hero-card">
          <div class="hero-value-row">
            <strong class="hero-value">{{ detail.headline }}</strong>
            <span class="hero-unit">{{ detail.unit }}</span>
          </div>
          <p>{{ detail.description }}</p>
        </section>

        <section v-if="detail.series?.length" class="detail-card chart-card">
          <div class="section-heading">
            <div>
              <p class="eyebrow">History</p>
              <h2>{{ rangeLabel }}</h2>
            </div>
            <span class="chart-hint">Hover a bar for its value</span>
          </div>
          <StatBarChart
            :data="detail.series"
            :max-value="detail.maxValue"
            :goal-line="detail.goalLine"
            :mode="detail.seriesMode"
          />
        </section>

        <section v-if="detail.breakdown?.length" class="detail-card">
          <div class="section-heading">
            <div>
              <p class="eyebrow">Current states</p>
              <h2>Word breakdown</h2>
            </div>
          </div>
          <div class="state-breakdown">
            <div
              v-for="item in detail.breakdown"
              :key="item.label"
              class="breakdown-row"
            >
              <span class="state-dot" :style="{ backgroundColor: item.color }"></span>
              <span class="breakdown-label">{{ item.label }}</span>
              <strong>{{ item.value.toLocaleString() }}</strong>
            </div>
          </div>
        </section>

        <section v-if="detail.summary?.length" class="summary-grid">
          <article
            v-for="item in detail.summary"
            :key="item.label"
            class="summary-card"
          >
            <span>{{ item.label }}</span>
            <strong>{{ item.value }}</strong>
          </article>
        </section>

        <section class="detail-card formula-card">
          <p class="eyebrow">How it is calculated</p>
          <p>{{ detail.formula }}</p>
        </section>
      </template>
    </div>
  </main>
</template>

<style scoped>
.stat-detail-page {
  min-height: 100vh;
  padding: 32px 200px 60px 275px;
  background: var(--app-bg);
  color: var(--text-color);
}

.stat-detail-shell {
  width: min(100%, 1100px);
  margin: 0 auto;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 26px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--accent);
  font: inherit;
  cursor: pointer;
}

.back-button span {
  font-size: 1.25rem;
}

.detail-header,
.section-heading,
.hero-value-row {
  display: flex;
  align-items: center;
}

.detail-header,
.section-heading {
  justify-content: space-between;
  gap: 18px;
}

.detail-header {
  margin-bottom: 18px;
}

h1,
h2,
p {
  margin-top: 0;
}

h1 {
  margin-bottom: 0;
  font-size: clamp(2rem, 4vw, 3.2rem);
  font-weight: 500;
  letter-spacing: -0.04em;
}

h2 {
  margin-bottom: 0;
  font-size: 1.3rem;
  font-weight: 500;
}

.eyebrow {
  margin-bottom: 6px;
  color: var(--accent);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.range-chip {
  flex: 0 0 auto;
  padding: 5px 10px;
  border-radius: 999px;
  background: var(--accent);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
}

.range-selector {
  display: inline-flex;
  flex: 0 0 auto;
  gap: 3px;
  padding: 3px;
  border-radius: 999px;
  background: var(--hover-bg);
}

.range-button {
  padding: 6px 11px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--text-color);
  font: inherit;
  font-size: 0.78rem;
  font-weight: 650;
  cursor: pointer;
  opacity: 0.68;
  transition: background 120ms ease, color 120ms ease, opacity 120ms ease;
}

.range-button:hover {
  opacity: 1;
}

.range-button.active {
  background: var(--accent);
  color: #fff;
  opacity: 1;
}

.range-button:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.hero-card,
.detail-card,
.summary-card,
.state-card {
  border-radius: 12px;
  background: var(--sidebar-bg);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.hero-card {
  margin-bottom: 16px;
  padding: 28px;
}

.hero-value-row {
  align-items: baseline;
  gap: 12px;
}

.hero-value {
  font-size: clamp(3rem, 8vw, 5.5rem);
  font-weight: 650;
  letter-spacing: -0.06em;
  line-height: 0.95;
}

.hero-unit {
  font-size: 1.15rem;
  font-weight: 500;
}

.hero-card > p {
  max-width: 720px;
  margin: 22px 0 0;
  color: var(--text-color);
  line-height: 1.6;
  opacity: 0.78;
}

.detail-card {
  margin-bottom: 16px;
  padding: 24px;
}

.chart-hint {
  font-size: 0.76rem;
  opacity: 0.62;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.summary-card {
  display: flex;
  min-height: 92px;
  padding: 20px;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
}

.summary-card span {
  font-size: 0.82rem;
  opacity: 0.7;
}

.summary-card strong {
  overflow-wrap: anywhere;
  font-size: 1.5rem;
  font-weight: 600;
}

.state-breakdown {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 24px;
  margin-top: 20px;
}

.breakdown-row {
  display: grid;
  grid-template-columns: 12px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
  border-bottom: 1px solid var(--hover-bg);
}

.state-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.breakdown-label {
  opacity: 0.76;
}

.formula-card p:last-child {
  max-width: 820px;
  margin-bottom: 0;
  line-height: 1.65;
  opacity: 0.78;
}

.state-card {
  min-height: 220px;
  padding: 28px;
}

.loading-line {
  width: 72%;
  height: 16px;
  margin-bottom: 18px;
  border-radius: 5px;
  background: var(--hover-bg);
  animation: pulse 1.1s ease-in-out infinite alternate;
}

.loading-title {
  width: 42%;
  height: 34px;
}

.loading-value {
  width: 28%;
  height: 70px;
}

.error-card p {
  opacity: 0.78;
}

.retry-button {
  padding: 9px 14px;
  border: 0;
  border-radius: 7px;
  background: var(--accent);
  color: #fff;
  font: inherit;
  cursor: pointer;
}

@keyframes pulse {
  from { opacity: 0.5; }
  to { opacity: 1; }
}

@media (max-width: 768px) {
  .stat-detail-page {
    padding: 76px 16px 48px;
  }

  .detail-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .hero-card,
  .detail-card {
    padding: 20px;
  }

  .summary-grid,
  .state-breakdown {
    grid-template-columns: 1fr;
  }

  .chart-hint {
    display: none;
  }
}
</style>
